import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Alert, Badge } from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2';
import apidefinitions from '../../../../core/api/apiDefinitions';
import { ServiceData } from './Service'; // Adjust path based on your file structure

interface ViewServiceModalProps {
  show: boolean;
  onHide: () => void;
  serviceId: number | null;
  statusColors: { [key: string]: string };
}

const ViewServiceModal: React.FC<ViewServiceModalProps> = ({ show, onHide, serviceId, statusColors }) => {
  const [service, setService] = useState<ServiceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (show && serviceId) {
      const fetchServiceData = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await apidefinitions.getVehicleServiceDataById(serviceId);
          if (response.data.status === 'S1000' && response.data.data) {
            setService(response.data.data);
          } else {
            setError(response.data.statusDescription || 'Failed to fetch service details.');
            Swal.fire({
              icon: 'error',
              title: 'Fetch Failed',
              text: response.data.statusDescription || 'Failed to fetch service details.',
              confirmButtonText: 'OK',
              customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
            });
          }
        } catch (err: any) {
          const errorMsg = err.response?.data?.statusDescription || 'Failed to fetch service details.';
          setError(errorMsg);
          Swal.fire({
            icon: 'error',
            title: 'Fetch Failed',
            text: errorMsg,
            confirmButtonText: 'OK',
            customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
          });
        } finally {
          setLoading(false);
        }
      };
      fetchServiceData();
    }
  }, [show, serviceId]);

  const handleImageClick = (url: string) => {
    setSelectedImageUrl(url);
    setShowImageModal(true);
  };

  const handleImageModalClose = () => {
    setShowImageModal(false);
    setSelectedImageUrl(null);
  };

  const formatDateTime = (date: string | null): string => {
    if (!date) return 'N/A';
    const parsedDate = moment(date);
    return parsedDate.isValid() ? parsedDate.format('MMM D, YYYY h:mm A') : 'Invalid Date';
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        className="rounded-xl shadow-2xl"
        aria-labelledby={`service-${serviceId || 'unknown'}`}
      >
        <Modal.Header closeButton className="bg-teal-600 text-white border-b-0 rounded-t-xl">
          <Modal.Title id={`service-${serviceId || 'unknown'}`} className="text-xl font-semibold">
            Service Details - ID: {serviceId || 'N/A'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 bg-white">
          {loading && (
            <Alert variant="info" className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-600 mr-2"></div>
              Loading service details...
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="rounded-lg p-4">
              {error}
            </Alert>
          )}
          {!loading && !error && !service && (
            <Alert variant="warning" className="rounded-lg p-4">
              No service details available.
            </Alert>
          )}
          {!loading && !error && service && (
            <div className="row">
              <div className="col-md-6">
                <h5 className="text-lg font-semibold text-gray-800 mb-3">Service Details</h5>
                <Table bordered hover className="table-striped">
                  <tbody>
                    <tr>
                      <td className="font-medium text-gray-700">Service ID</td>
                      <td>{service.id}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Vehicle ID</td>
                      <td>{service.vehicleId}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Service Type</td>
                      <td>{service.serviceType}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Supervisor ID</td>
                      <td>{service.supervisorId || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Service Date</td>
                      <td>{formatDateTime(service.serviceDate)}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Service KMs</td>
                      <td>{service.serviceKms}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Service Cost</td>
                      <td>Rs.{service.serviceCost.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Description</td>
                      <td
                        title={service.serviceDescription || 'N/A'}
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '250px',
                        }}
                      >
                        {service.serviceDescription
                          ? service.serviceDescription.length > 40
                            ? `${service.serviceDescription.slice(0, 40)}...`
                            : service.serviceDescription
                          : 'N/A'}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <h5 className="text-lg font-semibold text-gray-800 mb-3 mt-4">Status & Approval</h5>
                <Table bordered hover className="table-striped">
                  <tbody>
                    <tr>
                      <td className="font-medium text-gray-700">Service Status</td>
                      <td>
                        <Badge bg={statusColors[service.serviceStatus] || 'secondary'}>
                          {service.serviceStatus || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Approval Status</td>
                      <td>
                        <Badge bg={statusColors[service.approvalStatus || ''] || 'secondary'}>
                          {service.approvalStatus || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Approved By</td>
                      <td>{service.approvedBy || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Approved At</td>
                      <td>{formatDateTime(service.approvedAt)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="col-md-6">
                <h5 className="text-lg font-semibold text-gray-800 mb-3">Bill Photos</h5>
                {service.billPhotoUrls?.length ? (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {service.billPhotoUrls.map((image, index) => (
                      <Button
                        key={`bill-image-${index}`}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleImageClick(image)}
                        className="rounded-lg px-3 py-1 text-teal-600 border-teal-600 hover:bg-teal-100 hover:text-teal-700 transition-colors"
                      >
                        View Image {index + 1}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <Alert variant="warning" className="rounded-lg p-4">
                    No bill photos available.
                  </Alert>
                )}

                <h5 className="text-lg font-semibold text-gray-800 mb-3 mt-4">Serviced Parts</h5>
                <Table bordered hover className="table-striped">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Serial No</th>
                      <th>Price</th>
                      <th>Warranty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {service.servicedVehicleParts.length ? (
                      service.servicedVehicleParts.map((part, index) => (
                        <tr key={index}>
                          <td
                            title={part.comment ? `Comment: ${part.comment}` : part.name}
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '150px',
                            }}
                          >
                            {part.name}
                          </td>
                          <td>{part.serialNo}</td>
                          <td>Rs.{part.price.toFixed(2)}</td>
                          <td>{part.warrantyPeriod} months</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4}>No serviced parts available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <h5 className="text-lg font-semibold text-gray-800 mb-3 mt-4">Audit Information</h5>
                <Table bordered hover className="table-striped">
                  <tbody>
                    <tr>
                      <td className="font-medium text-gray-700">Created By</td>
                      <td>{service.createdBy || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700">Created Date</td>
                      <td>{formatDateTime(service.createdAt)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className=" border-t-0 rounded-b-xl p-4">
          <Button
            variant="outline-primary"
            onClick={onHide}
            disabled={loading}
            className="border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-gray-800 rounded-lg px-4 py-2 transition-colors"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Viewer Modal */}
      <Modal
        show={showImageModal}
        onHide={handleImageModalClose}
        centered
        className="rounded-xl shadow-2xl"
      >
        <Modal.Header closeButton className="bg-teal-600 text-white border-b-0 rounded-t-xl">
          <Modal.Title className="text-lg font-semibold">Bill Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 bg-white flex justify-center items-center">
          {selectedImageUrl ? (
            <img
              src={selectedImageUrl}
              alt="Bill"
              className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-md"
              onError={() => {
                Swal.fire({
                  icon: 'error',
                  title: 'Image Load Failed',
                  text: 'Unable to load the image. Please check the URL or try again.',
                  confirmButtonText: 'OK',
                  customClass: { confirmButton: 'bg-teal-600 text-white hover:bg-teal-700 rounded-lg px-4 py-2' },
                });
                handleImageModalClose();
              }}
            />
          ) : (
            <Alert variant="warning" className="w-full text-center rounded-lg p-4">
              No image available
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className=" rounded-b-xl p-4">
          <Button
            variant="outline-primary"
            onClick={handleImageModalClose}
            className="border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-gray-800 rounded-lg px-4 py-2 transition-colors"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ViewServiceModal;